import type { ForecastingRow } from '@/api/forecasting';
import { parseMetricNumber } from './forecastingNumbers';

const DYNAMIC_FORECAST_LABEL = 'dynamic forecast';

/** True when the Forecast Stage column is "Dynamic Forecast". */
export function isDynamicForecastStage(stage?: string): boolean {
  const normalized = stage?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
  return normalized === DYNAMIC_FORECAST_LABEL;
}

const BUDGET_STATUS = 'budgetv2';

/** Looker: Status IN ("Budgetv2"). */
export function isBudgetRow(row: ForecastingRow): boolean {
  return row.Status?.trim().toLowerCase() === BUDGET_STATUS;
}

function hasExplicitRevenueWeighting(row: ForecastingRow): boolean {
  return row.Revenue_Weighting != null && row.Revenue_Weighting !== '';
}

/** Looker: Revenue Weighting is 1 or 0 (only used when present on the API row). */
export function parseRevenueWeighting(value?: string): number {
  if (value == null || value === '') return 1;
  const n = parseMetricNumber(value);
  if (n === 0) return 0;
  if (n === 1) return 1;

  const s = value.trim().toLowerCase();
  if (s === 'true' || s === 'yes') return 1;
  if (s === 'false' || s === 'no') return 0;
  return n > 0 ? 1 : 0;
}

function revenueMultiplier(row: ForecastingRow): number {
  if (!hasExplicitRevenueWeighting(row)) return 1;
  return parseRevenueWeighting(row.Revenue_Weighting);
}

/** @deprecated Use isDynamicForecastStage */
export function isDynamicForecastEligible(row: ForecastingRow): boolean {
  return isDynamicForecastStage(row.Forecast_Stage);
}

/**
 * Dynamic Forecast Revenue:
 * Sum Net Rev (Rec) for every row where Forecast Stage = "Dynamic Forecast".
 * If the API sends Revenue_Weighting, apply it (1 or 0); otherwise count the row in full.
 */
export function getDynamicForecastRevenue(row: ForecastingRow): number {
  if (!isDynamicForecastStage(row.Forecast_Stage)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_) * revenueMultiplier(row);
}

export function getDynamicForecastGp(row: ForecastingRow): number {
  if (!isDynamicForecastStage(row.Forecast_Stage)) return 0;
  return parseMetricNumber(row.Gross_Profit) * revenueMultiplier(row);
}

/** Looker: WHEN Status IN ("Budgetv2") THEN Net Rev (Rec) */
export function getBudgetRevenue(row: ForecastingRow): number {
  if (!isBudgetRow(row)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_);
}

/** Looker: WHEN Status IN ("Budgetv2") THEN Gross Profit */
export function getBudgetGp(row: ForecastingRow): number {
  if (!isBudgetRow(row)) return 0;
  return parseMetricNumber(row.Gross_Profit);
}

const TARGET_STATUS = 'target';

/** The sales team's Target rows (Status "Target"), set above the Budgetv2
 *  budget. The quarterly page measures against these; the full-year page
 *  keeps Budget. */
export function isTargetRow(row: ForecastingRow): boolean {
  return row.Status?.trim().toLowerCase() === TARGET_STATUS;
}

export function getTargetRevenue(row: ForecastingRow): number {
  if (!isTargetRow(row)) return 0;
  return parseMetricNumber(row.Net_Rev__Rec_);
}

export function getTargetGp(row: ForecastingRow): number {
  if (!isTargetRow(row)) return 0;
  return parseMetricNumber(row.Gross_Profit);
}

/** @deprecated Weighting defaults to 1 for Dynamic Forecast rows unless API provides the field. */
export function getRevenueWeighting(row: ForecastingRow): number {
  if (!isDynamicForecastStage(row.Forecast_Stage)) return 0;
  return revenueMultiplier(row);
}

/** @deprecated */
export function computeRevenueWeighting(row: ForecastingRow): number {
  return getRevenueWeighting(row);
}
